import React, { useMemo, useState, useEffect } from "react";
import moment from "moment-timezone";
import { Button } from "@/components/ui/button";
import { Loader } from "lucide-react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import { 
    CreateCropSettingValidation, 
    UpdateCropSettingValidation, 
    type CropSetting 
} from "@/lib/api/risk/profiles/cropSettings";
import { AcceptedCommodity, AcceptedCommodityType, acceptedCommodityTypes } from "@/lib/api/risk/profiles";
import { SelectOptionItem } from "@/lib/models/ui";
import { useListCustomers } from "@/lib/api/customer";
import { formulateOptionMonthsWithOlder } from "@/lib/api/common";
import { FormInput, FormCombobox } from "../../FormComponents";
import { CommonDestinationTimezone } from "@/lib/utils";

export type CreateFormData = z.infer<typeof CreateCropSettingValidation>;
export type UpdateFormData = z.infer<typeof UpdateCropSettingValidation>;
export type CropSettingsFormData = CreateFormData | UpdateFormData;

interface CropSettingsFormPropsBase {
    onCancel?: () => void;
    showCancelButton?: boolean;
    isSubmitting?: boolean;
}

interface CreateFormProps extends CropSettingsFormPropsBase {
    mode: "create";
    initialData?: CropSetting | null;
    onSubmit: (data: CreateFormData) => Promise<void>;
}

interface UpdateFormProps extends CropSettingsFormPropsBase {
    mode: "update";
    initialData: CropSetting | null;
    onSubmit: (data: UpdateFormData) => Promise<void>;
}

type CropSettingsFormProps = CreateFormProps | UpdateFormProps;

export function CropSettingsForm(props: CropSettingsFormProps) {
    const [initialOptionMonth] = useState(["F", moment.tz(CommonDestinationTimezone).subtract(1, "year").year().toString()].join(""))
    const minimalCustomersListResult = useListCustomers({ minimal: true, hasRiskProfile: true });
    const minimalCustomersList = minimalCustomersListResult?.data?.data || [];

    const { mode, initialData, onSubmit, onCancel, showCancelButton = true, isSubmitting = false } = props;
    
    // Use the appropriate schema based on mode
    const formSchema = mode === "create" ? CreateCropSettingValidation : UpdateCropSettingValidation;

    // Current year for default
    const currentYear = new Date().getFullYear();

    // Create form with proper typing
    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: "onChange",
        defaultValues: mode === "create" ? {
            commodity_name: initialData?.commodity_name || "",
            commodity_type: initialData?.commodity_type || "",
            crop_year: initialData?.crop_year || currentYear,
            customer_id: initialData?.customer_id || "",
            option_month: initialData?.option_month || "",
            implied_basis: initialData?.implied_basis?.toString() || "",
            is_enabled: initialData?.is_enabled ?? true,
        } : {
            option_month: initialData?.option_month || "",
            implied_basis: initialData?.implied_basis?.toString() || "",
            is_enabled: initialData?.is_enabled ?? true,
        },
    });

    // Reset form when initialData changes
    useEffect(() => {
        if (initialData) {
            if (mode === "create") {
                form.reset({
                    commodity_name: initialData.commodity_name || "",
                    commodity_type: initialData.commodity_type || "",
                    crop_year: initialData.crop_year || currentYear,
                    customer_id: initialData.customer_id,
                    option_month: initialData.option_month,
                    implied_basis: initialData.implied_basis?.toString() || "",
                    is_enabled: initialData.is_enabled ?? true,
                });
            } else {
                form.reset({
                    option_month: initialData.option_month,
                    implied_basis: initialData.implied_basis?.toString() || "",
                    is_enabled: initialData.is_enabled ?? true,
                });
            }
        }
    }, [initialData, form, mode, currentYear]);

    // Auto-set commodity_type when commodity_name is selected
    const selectedCommodityName = form.watch('commodity_name');
    useEffect(() => {
        if (mode === "create" && selectedCommodityName) {
            const commodityType = AcceptedCommodityType[selectedCommodityName as AcceptedCommodity];
            // Set the commodity type even if it's an empty string (for commodities like Sorghum)
            form.setValue('commodity_type', commodityType || "");
        }
    }, [selectedCommodityName, form, mode]);

    const {
        commodityOptions,
        customerOptions,
        optionMonthOptions,
    } = useMemo(() => {
        // Prepare options for dropdowns (using acceptedCommodityTypes like CropProfileForm)
        const commodityOptions: SelectOptionItem<string>[] = acceptedCommodityTypes.map(commodity => ({
            value: commodity,
            label: commodity
        }));

        const customerOptions: SelectOptionItem<string>[] = minimalCustomersList.map((customer: { id: string; long_name: string }) => ({
            value: customer.id,
            label: customer.long_name
        }));

        const optionMonthOptions = formulateOptionMonthsWithOlder(initialOptionMonth);

        return {
            commodityOptions,
            customerOptions,
            optionMonthOptions,
        }
    }, [])

    // Generate crop year options (current year and next 5 years)
    const cropYearOptions: SelectOptionItem<string>[] = Array.from({ length: 10 }, (_, i) => {
        const year = currentYear + i;
        return {
            value: year.toString(),
            label: year.toString()
        };
    });

    const handleSubmit = form.handleSubmit((data) => {
        if (mode === "create") {
            // Derive commodity_type from commodity_name for create mode
            const commodityType = AcceptedCommodityType[data.commodity_name as AcceptedCommodity] || "";
            const createData = {
                ...data,
                commodity_type: commodityType
            } as CreateFormData;
            return onSubmit(createData);
        } else {
            return onSubmit(data as UpdateFormData);
        }
    });

    return (
        <Form {...form}>
            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
                    {mode === "create" && (
                        <>
                            <FormField
                                control={form.control}
                                name="commodity_name"
                                render={({ field }) => (
                                    <FormItem className="grid">
                                        <FormLabel>Commodity *</FormLabel>
                                        <FormControl>
                                            <FormCombobox
                                                options={commodityOptions}
                                                value={field.value || ""}
                                                onValueChange={field.onChange}
                                                dwimOnChange={true}
                                                isModal={true}
                                                placeholder="Select commodity..."
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="customer_id"
                                render={({ field }) => (
                                    <FormItem className="grid">
                                        <FormLabel>Customer</FormLabel>
                                        <FormControl>
                                            <FormCombobox
                                                options={customerOptions}
                                                value={field.value || ""}
                                                onValueChange={field.onChange}
                                                dwimOnChange={true}
                                                isModal={true}
                                                placeholder="Select customer..."
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </>
                    )}

                    <FormField
                        control={form.control}
                        name="option_month"
                        render={({ field }) => (
                            <FormItem className="grid">
                                <FormLabel>Option Month</FormLabel>
                                <FormControl>
                                    <FormCombobox
                                        options={optionMonthOptions}
                                        value={field.value || ""}
                                        onValueChange={field.onChange}
                                        dwimOnChange={true}
                                        isModal={true}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="implied_basis"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Implied Basis</FormLabel>
                                <FormControl>
                                    <FormInput
                                        {...field}
                                        type="number"
                                        step="0.0001"
                                        placeholder="0.0000"
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                </div>

                <div className="flex justify-end gap-2">
                    {showCancelButton && (
                        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
                            Cancel
                        </Button>
                    )}
                    <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting && <Loader className="w-4 h-4 mr-2 animate-spin" />}
                        {mode === "create" ? "Create" : "Update"} Setting
                    </Button>
                </div>
            </form>
        </Form>
    );
}
